import React from 'react';
import type { ContractDetail } from '../types';
import { isContractStatusCancellable } from '@/lib/contractUtils';

interface UseContractSelectionProps {
  /** Array of contracts currently displayed */
  displayedContracts: ContractDetail[];
  /** Whether customer selection is required (multiple customers exist) */
  shouldShowCustomerSelection: boolean;
  /** Names of customers who have currently selected contracts */
  selectedContractCustomers: string[];
}

interface UseContractSelectionReturn {
  /** Set of currently selected contract numbers */
  selectedContracts: Set<string>;
  /** Whether validation has been attempted (for showing validation errors) */
  validationAttempted: boolean;
  /** Current validation error message, if any */
  validationErrorMessage: string | null;
  /** Selection status for "select all" checkbox */
  selectionStatus: 'all' | 'none' | 'some';
  /** Function to handle contract selection changes */
  handleContractSelectionChange: (
    contractNumber: string,
    isSelected: boolean
  ) => void;
  /** Function to clear all selections */
  clearSelections: () => void;
  /** Function to attempt validation (call before submitting) */
  attemptValidation: () => boolean;
  /** Function to clear validation state */
  clearValidation: () => void;
}

/**
 * Custom hook for managing contract selection and validation logic.
 *
 * This hook encapsulates all contract selection state and validation logic including:
 * - Contract selection state management
 * - Validation state for submit attempts
 * - Selection status calculation for "select all" functionality
 * - Validation logic for quote requests
 *
 * @param props Configuration object for contract selection behavior
 * @returns Object containing contract selection state and handlers
 */
export function useContractSelection({
  displayedContracts,
  shouldShowCustomerSelection,
  selectedContractCustomers,
}: UseContractSelectionProps): UseContractSelectionReturn {
  const [selectedContracts, setSelectedContracts] = React.useState<Set<string>>(
    new Set()
  );
  const [validationAttempted, setValidationAttempted] = React.useState(false);

  // Calculate selection status for "select all" checkbox
  const selectionStatus: 'all' | 'none' | 'some' = React.useMemo(() => {
    if (!displayedContracts || displayedContracts.length === 0) {
      return 'none';
    }

    // Only count selectable contracts since some contracts cannot be selected
    const selectableContracts = displayedContracts.filter((contract) =>
      isContractStatusCancellable(contract.status)
    );

    if (selectableContracts.length === 0) {
      return 'none';
    }

    if (selectedContracts.size === 0) {
      return 'none';
    }

    if (selectedContracts.size === selectableContracts.length) {
      return 'all';
    }

    return 'some';
  }, [selectedContracts, displayedContracts]);

  // Get validation error message - only show after validation has been attempted
  const validationErrorMessage = React.useMemo(() => {
    if (!validationAttempted) {
      return null;
    }

    // If customer selection is required and multiple customers are in selection
    if (shouldShowCustomerSelection && selectedContractCustomers.length > 1) {
      return 'Please select contracts from only one customer, or choose a specific customer to filter the results.';
    }

    return null;
  }, [
    validationAttempted,
    shouldShowCustomerSelection,
    selectedContractCustomers.length,
  ]);

  const handleContractSelectionChange = React.useCallback(
    (contractNumber: string, isSelected: boolean) => {
      setSelectedContracts((prevSelected) => {
        const newSelected = new Set(prevSelected);
        if (isSelected) {
          newSelected.add(contractNumber);
        } else {
          newSelected.delete(contractNumber);
        }
        return newSelected;
      });

      // Clear validation state when contract selection changes
      setValidationAttempted(false);
    },
    []
  );

  const clearSelections = React.useCallback(() => {
    setSelectedContracts(new Set());
    setValidationAttempted(false);
  }, []);

  const clearValidation = React.useCallback(() => {
    setValidationAttempted(false);
  }, []);

  const attemptValidation = React.useCallback((): boolean => {
    setValidationAttempted(true);

    // Check if there are validation errors
    if (shouldShowCustomerSelection && selectedContractCustomers.length > 1) {
      return false; // Validation failed
    }

    return true; // Validation passed
  }, [shouldShowCustomerSelection, selectedContractCustomers.length]);

  return {
    selectedContracts,
    validationAttempted,
    validationErrorMessage,
    selectionStatus,
    handleContractSelectionChange,
    clearSelections,
    attemptValidation,
    clearValidation,
  };
}
